<template>
  <div>
    <p class="category">Trigger Identification</p>
    <div class="container text-left">
      <p>
        Nowadays, rumors tend to spread quickly and widely on the Internet, and automatically verifying rumors has
        become an urgent need for individuals and society.
        From social platforms (such as Twitter and Weibo), we can crawl information cascades that consist of source
        posts and corresponding reposts.
        The task of <strong>rumor verification</strong> aims at classifying rumor cascade as <strong>true</strong>,
        <strong>false</strong> or <strong>unverified</strong>.
        However, predicting at cascade level is too coarse to organize massive messages and opinions. Therefore, Fudan
        DISC Lab supplement message-level annotations to exiting rumor corpus PHEME and propose a sub-task named
        <strong>trigger
          identification</strong>, which aims at identifying messages that have prominent effects on rumor proliferation
        and
        dominate the judgment of cascade credibility.
        We summarize message roles into 4 categories, i.e. <strong>amplify</strong>, <strong>deny</strong>, <strong>clarify</strong>
        and <strong>null</strong>.
        Amplify indicates tweets that initiate new concerns or enlarge the discussion scale related to the social event.
        Deny means presenting doubt or rejection towards previous messages. Clarify introduces factual
        or substantial information.
        Other messages are left as null which means they are insignificant for rumor propagation or verification.
      </p>
      <p>
        The extended <strong>dataset</strong> contains 1,929 cascades and 26,871 messages annotated.
        Train, validation, test (phase 1) and test (phase 2) sets are split randomly with a proportion of 7:1:1:1.
        The <strong>baseline model</strong> applies bert-base-uncased pretrained model to obtain message representation
        and employs mean
        pooling to acquire cascade representation.
        We adopt macro F1 score as <strong>evaluation metrics</strong>.
      </p>
      <p>
        The dataset is organized in the csv format.
        The table below presents an instance of information cascades, and the meaning of related fields.
      </p>
      <div class="my-3">
        <el-table :data="tableData" style="width: 100%" border :default-sort="{prop: 'mid', order: 'ascending'}">
          <el-table-column prop="cid" label="cid" min-width="170"></el-table-column>
          <el-table-column prop="mid" label="mid" min-width="170"></el-table-column>
          <el-table-column prop="pid" label="pid" min-width="170"></el-table-column>
          <el-table-column prop="time" label="time" min-width="110"></el-table-column>
          <el-table-column prop="date" label="date" min-width="155"></el-table-column>
          <el-table-column prop="content" label="content" min-width="400"></el-table-column>
          <el-table-column prop="verify" label="verify" min-width="90"></el-table-column>
          <el-table-column prop="trigger" label="trigger" min-width="90"></el-table-column>
        </el-table>
      </div>
      <div class="my-3">
        <el-table :data="tableField" style="width: 100%" border>
          <el-table-column prop="field" label="Field Name" min-width="200"></el-table-column>
          <el-table-column prop="meaning" label="Meaning" min-width="400"></el-table-column>
        </el-table>
      </div>
      <p>
        Data resources and baseline implementation can be obtained in following links:
      </p>
      <div>
        <span class="h6 mr-2">Data resources: </span>
        <a href="/resource_social22/data/data_trigger.zip">data_trigger.zip</a>
      </div>
      <div>
        <span class="h6 mr-2">Baseline Implementation: </span>
        <a href="/resource_social22/data/src_trigger.zip">src_trigger.zip</a>
      </div>
      <p class="mt-3">Uploading results should be saved as a <strong>.json</strong> file like this:</p>

      <div>
        <json-tree :raw="data2str(responseData)"></json-tree>
      </div>
      <p>* If using the framework of the source code, a
        file named <strong>test_result.json</strong> will be automatically generated.</p>
    </div>
  </div>
</template>

<script>
import JsonTree from "vue-json-tree";

export default {
  name: "taskTrigger",
  components: {JsonTree},
  data() {
    return {
      tableData: [
        {
          'event': 'ottawashooting',
          'cid': '524965775036387329',
          'mid': '524965775036387329',
          'pid': 'None',
          'time': 1413996568.0,
          'date': '2014-10-22 16:49:28',
          'content': 'Hero. @affanchowdhry: Kevin Vickers, head of security in parliament, being credited for taking down shooter #Ottawa http://t.co/rVYetE0KYq"',
          'verify': 1,
          'trigger': 1
        },
        {
          'event': 'ottawashooting',
          'cid': '524965775036387329',
          'mid': '524972195978964993',
          'pid': '524965775036387329',
          'time': 1413998099.0,
          'date': '2014-10-22 17:14:59',
          'content': '@LindaFrum @LeBlancJpl @affanchowdhry good for Vickers',
          'verify': 1,
          'trigger': 0
        },
        {
          'event': 'ottawashooting',
          'cid': '524965775036387329',
          'mid': '524974387125317633',
          'pid': '524965775036387329',
          'time': 1413998622.0,
          'date': '2014-10-22 17:23:42',
          'content': '@LindaFrum @affanchowdhry  absolutely!',
          'verify': 1,
          'trigger': 0
        },
        {
          'event': 'ottawashooting',
          'cid': '524965775036387329',
          'mid': '525005729938554882',
          'pid': '524965775036387329',
          'time': 1414006094.0,
          'date': '2014-10-22 19:28:14',
          'content': '@LindaFrum @affanchowdhry The real hero is the soldier who lost his life doing his job and serving his country.',
          'verify': 1,
          'trigger': 1
        },
        {
          'event': 'ottawashooting',
          'cid': '524965775036387329',
          'mid': '525015279387299840',
          'pid': '524965775036387329',
          'time': 1414008371.0,
          'date': '2014-10-22 20:06:11',
          'content': '@LindaFrum @affanchowdhry.. \nThis was an attack on all Canadians whatever stripe \nObviously these despicable acts are from cowardly cretins.',
          'verify': 1,
          'trigger': 0
        }],
      tableField: [
        {field: 'cid', meaning: 'ID of the cascade'},
        {field: 'mid', meaning: 'ID of the message'},
        {field: 'pid', meaning: 'ID of the parent message (where the message repost from)'},
        {field: 'time', meaning: 'Posting time of the message'},
        {field: 'date', meaning: 'Posting date of the message'},
        {field: 'content', meaning: 'The content of the message'},
        {field: 'verify', meaning: 'The verification label for the cascade (0-false 1-true 2-unverified)'},
        {field: 'trigger', meaning: 'The trigger label for the message (0-null 1-amplify 2-deny 3-clarify)'},
      ],
      responseData: {
        'trigger': {'mid': [1, 2, 3], 'preds': [0, 0, 0]},
        'verify': {'cid': [1, 2, 3], 'preds': [0, 0, 0]}
      }
    }
  },
  methods: {
    data2str(data) {
      return JSON.stringify(data);
    },
  },

}
</script>

<style scoped>

</style>